import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import questionnaireModule from './questionnaire.json'
Vue.use(Vuex)

const state = {
	start: null,
	questions: null,
	questionsLookup: {},
	answers: {},
	// designer TODO separate module?
	selectedQuestion: null
}

const mutations = {
	LOAD_QUESTIONNAIRE (state, {start, questions, questionsLookup}) {
		state.start = start
		state.questions = questions
		state.questionsLookup = questionsLookup
	},
	LOAD_ANSWERS (state, answers) {
		state.answers = answers
	},
	SET_ANSWER (state, {id, answer}) {
		Vue.set(state.answers, id, answer)
	},
	SELECT_QUESTION (state, question) {
		state.selectedQuestion = question
	},
	UPDATE_QUESTION (state, {question, update}) {
		if (update.id && state.questionsLookup[update.id]) return // id already taken
		Object.assign(question, update)
	},
	ADD_QUESTION (state, question) {
		if (state.questionsLookup[question.id]) return // id already taken
		state.questions.push(question)
		state.questionsLookup[question.id] = question
	},
	CONNECT_NEXT (state, {from, to}) {
		from.next = to
	},
	ADD_OPTION (state, {question, option}) {
		if (!question.answer.options) {
			question.answer.options = []
		}
		question.answer.options.push(option)
	},
	UPDATE_OPTION (state, {option, update}) {
		Object.assign(option, update)
	}
}

const getters = {
	currentQuestion (state) {
		let question = state.start
		while (question) {
			const answer = state.answers[question.id]
			const show = !question.showIf || new Function('answers', this.question.question.function)(state.answers)
			if (!answer && show) return question
			if (question.next) {
				question = question.next
			} else if (question.answer.type === 'chooseOne') {
				const option = question.answer.options.find(option => isEqual(option.value, answer))
				question = option.next
			} else {
				return null
			}
		}
	},
	serializedQuestionnaire (state) {
		if (!state.questions) return null
		const questions = state.questions.map(question => {
			const serializedQuestion = {id: question.id} // force id as first attribute
			Object.assign(serializedQuestion, cloneDeep(question))
			serializedQuestion.next = question.next?.id
			for (const option of serializedQuestion.answer.options || []) {
				option.next = option.next?.id
			}
			return serializedQuestion
		})
		return JSON.stringify({
			start: state.start.id,
			questions
		}, null, '\t')
	}
}

const actions = {
	loadQuestionnaire ({commit}) {
		let questionnaire = cloneDeep(questionnaireModule)
		if (localStorage.questionnaire) {
			questionnaire = JSON.parse(localStorage.questionnaire)
		}

		const questionsLookup = questionnaire.questions.reduce((acc, question) => {
			acc[question.id] = question
			return acc
		}, {})

		// deserialize definition
		for (const question of questionnaire.questions) {
			question.next = questionsLookup[question.next] || null
			for (const option of question.answer.options || []) {
				option.next = questionsLookup[option.next] || null
			}
			question.showIf = question.showIf || null
		}

		commit('LOAD_QUESTIONNAIRE', {
			questions: questionnaire.questions,
			questionsLookup,
			start: questionsLookup[questionnaire.start]
		})
	},
	init ({state, commit, dispatch}) {
		dispatch('loadQuestionnaire')
		if (localStorage.savedAnswers) {
			const answers = JSON.parse(localStorage.savedAnswers)
			for (const [key, value] of Object.entries(answers)) {
				if (state.questionsLookup[key].answer.type === 'date') {
					answers[key] = moment(value)
				}
			}
			commit('LOAD_ANSWERS', answers)
		}
	},
	reset ({commit}) {
		localStorage.removeItem('savedAnswers')
		commit('LOAD_ANSWERS', {})
	},
	anwserCurrentQuestion ({state, commit, getters}, answer) {
		commit('SET_ANSWER', {
			id: getters.currentQuestion.id,
			answer
		})
		localStorage.setItem('savedAnswers', JSON.stringify(state.answers))
	},
	createQuestionAndConnect ({state, commit, dispatch}, connectFrom) {
		let id = 1
		while (state.questionsLookup[`question${id}`]) {
			id++
		}
		const question = {
			id: `question${id}`,
			question: '',
			answer: {
				type: null,
				options: null
			},
			next: null,
			showIf: null
		}

		commit('ADD_QUESTION', question)
		commit('CONNECT_NEXT', {from: connectFrom, to: question})
		dispatch('save')
		return question
	},
	updateQuestion ({commit, dispatch}, {question, update}) {
		commit('UPDATE_QUESTION', {question, update})
		dispatch('save')
	},
	addOption ({commit, dispatch}, {question, option}) {
		commit('ADD_OPTION', {question, option})
		dispatch('save')
	},
	updateOption ({commit, dispatch}, {option, update}) {
		commit('UPDATE_OPTION', {option, update})
		dispatch('save')
	},
	connectNext ({commit, dispatch}, {from, to}) {
		commit('CONNECT_NEXT', {from, to})
		dispatch('save')
	},
	save ({getters}) {
		localStorage.setItem('questionnaire', getters.serializedQuestionnaire)
	},
	resetQuestionnaire ({dispatch}) {
		localStorage.removeItem('questionnaire')
		dispatch('loadQuestionnaire')
	}
}

const store = new Vuex.Store({
	state,
	mutations,
	getters,
	actions
})

export default store
